// pushService.js - Service for browser push notifications

const API_BASE_URL = (() => {
  const envUrl = process.env.REACT_APP_API_URL || '/api';
  // Backend routes are /api/push/... (no /v1 prefix)
  return envUrl.startsWith('http') ? `${envUrl}/api` : envUrl;
})();

// Store the VAPID public key
let vapidPublicKey = null;

// Convert base64 string to Uint8Array for subscription
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

// Fetch VAPID public key from server
async function fetchVapidPublicKey() {
  if (vapidPublicKey) {
    console.log('[PushService] Using cached VAPID key');
    return vapidPublicKey;
  }

  try {
    const token = localStorage.getItem('token');
    console.log('[PushService] fetchVapidPublicKey called, token:', token ? 'present' : 'missing');
    if (!token) {
      console.log('[PushService] No auth token available yet');
      return null;
    }

    console.log('[PushService] Fetching VAPID key from server...');
    const response = await fetch(`${API_BASE_URL}/push/vapid-key`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    const data = await response.json();
    if (data.success && data.data && data.data.publicKey) {
      vapidPublicKey = data.data.publicKey;
      return vapidPublicKey;
    }
  } catch (error) {
    console.error('[PushService] Error fetching VAPID key:', error);
  }

  return null;
}

// Subscribe to push notifications
async function subscribeToPush() {
  console.log('[PushService] subscribeToPush() called - starting push subscription');
  console.log('[PushService] Notification in window:', 'Notification' in window);
  try {
    // Check if push is supported
    console.log('[PushService] Checking push support...');
    if (!('serviceWorker' in navigator)) {
      console.warn('[PushService] Service workers not supported');
      return null;
    }
    console.log('[PushService] serviceWorker: supported');

    if (!('PushManager' in window)) {
      console.warn('[PushService] PushManager not supported');
      return null;
    }
    console.log('[PushService] PushManager: supported');

    // Wait for auth token (up to 10 seconds)
    let token = localStorage.getItem('token');
    let retries = 0;
    while (!token && retries < 20) {
      await new Promise(r => setTimeout(r, 500));
      token = localStorage.getItem('token');
      retries++;
    }

    if (!token) {
      console.log('[PushService] No auth token available after waiting');
      return null;
    }

    console.log('[PushService] Token available, proceeding with push setup');

    // Check notification permission
    console.log('[PushService] Checking notification permission...');
    let permission = Notification.permission;
    console.log('[PushService] Current permission:', permission);
    if (permission === 'default') {
      console.log('[PushService] Requesting permission...');
      permission = await Notification.requestPermission();
      console.log('[PushService] Permission result:', permission);
    }

    if (permission !== 'granted') {
      console.warn('[PushService] Notification permission not granted:', permission);
      return null;
    }

    // Get VAPID public key
    const publicKey = await fetchVapidPublicKey();
    if (!publicKey) {
      console.error('[PushService] No VAPID public key available');
      return null;
    }

    // Register service worker and wait for it to be ready
    const registration = await navigator.serviceWorker.register('/sw.js');
    console.log('[PushService] Service worker registered, waiting for it to be ready...');

    // Wait for the service worker to be ready (active)
    const readyRegistration = await navigator.serviceWorker.ready;
    console.log('[PushService] Service worker ready:', readyRegistration);

    // Check for existing subscription in the browser
    const existingSubscription = await readyRegistration.pushManager.getSubscription();
    let subscription = existingSubscription;

    // Check if existing subscription has valid keys
    const hasValidKeys = subscription &&
                         subscription.keys &&
                         subscription.keys.p256dh &&
                         subscription.keys.auth;

    // If no existing subscription or invalid keys, create a new one
    if (!subscription || !hasValidKeys) {
      // If there's an existing subscription with invalid keys, unsubscribe first
      if (subscription) {
        console.log('[PushService] Existing subscription has invalid keys, unsubscribing...');
        await subscription.unsubscribe();
      }
      console.log('[PushService] Creating new subscription...');
      subscription = await readyRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
      console.log('[PushService] New subscription created');
    } else {
      console.log('[PushService] Existing subscription found with valid keys, reusing it...');
    }

    console.log('[PushService] Push subscription:', subscription);

    // Get keys using getKey() method (returns ArrayBuffer)
    const p256dhKey = subscription.getKey('p256dh');
    const authKey = subscription.getKey('auth');
    console.log('[PushService] P256DH key:', p256dhKey);
    console.log('[PushService] Auth key:', authKey);

    // Convert ArrayBuffer to base64 string
    const arrayBufferToBase64 = (buffer) => {
      let binary = '';
      const bytes = new Uint8Array(buffer);
      for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      return window.btoa(binary);
    };

    const p256dh = p256dhKey ? arrayBufferToBase64(p256dhKey) : null;
    const auth = authKey ? arrayBufferToBase64(authKey) : null;
    console.log('[PushService] P256DH base64:', p256dh);
    console.log('[PushService] Auth base64:', auth);

    // Final validation - should always have keys now
    if (!p256dh || !auth) {
      console.error('[PushService] Subscription missing keys after creation');
      return null;
    }

    // Send subscription to server (the backend will create or update)
    const response = await fetch(`${API_BASE_URL}/push/subscribe`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        endpoint: subscription.endpoint,
        keys: {
          p256dh: p256dh,
          auth: auth,
        },
      }),
    });

    const data = await response.json();
    if (data.success) {
      console.log('[PushService] Subscribed to push notifications');
      return subscription;
    } else {
      console.error('[PushService] Failed to subscribe:', data.error);
      return null;
    }
  } catch (error) {
    console.error('[PushService] Error subscribing to push:', error);
    return null;
  }
}

// Unsubscribe from push notifications
async function unsubscribeFromPush() {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      await subscription.unsubscribe();

      // Notify server
      const token = localStorage.getItem('token');
      await fetch(`${API_BASE_URL}/push/unsubscribe`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      console.log('[PushService] Unsubscribed from push notifications');
    }
  } catch (error) {
    console.error('[PushService] Error unsubscribing from push:', error);
  }
}

// Check if push is supported
function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window;
}

// Check current notification permission
function getNotificationPermission() {
  if (!('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission;
}

export default {
  subscribeToPush,
  unsubscribeFromPush,
  isPushSupported,
  getNotificationPermission,
  fetchVapidPublicKey,
};
